import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet";
import { Clock, MapPin, ChevronRight, Users, MessageCircle } from "lucide-react";
import PageTransition from "../components/PageTransition";
import PublicHeader from "../components/PublicHeader";
import SeoFooter from "../components/SeoFooter";

export default function ToursPage() {
  const [tourPackages, setTourPackages] = useState([]);
  useEffect(() => {
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/tours`).then((r) => setTourPackages(r.data)).catch(() => {});
  }, []);
  return (
    <PageTransition>
      <div className="min-h-screen bg-background" data-testid="tours-page">
        <Helmet>
          <title>Private Tour Packages Brisbane & Gold Coast | Winery Tours & Brewery Crawls | Limo Near Me</title>
          <meta name="description" content="All-inclusive hosted day-out packages across South East Queensland — Mount Tamborine wine trail, Brisbane brewery crawls, Scenic Rim long lunches, Byron Bay day trips. Private bus, driver and itinerary sorted. Book online." />
          <link rel="canonical" href="https://www.limonearme.com.au/tours" />
        </Helmet>
        <PublicHeader />

        <section className="pt-32 pb-14 bg-gradient-to-b from-amber-50 via-white to-white text-center">
          <div className="max-w-4xl mx-auto px-4">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <p className="text-sm font-semibold tracking-[0.2em] uppercase text-amber-700 mb-4">All-inclusive experiences</p>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-foreground mb-4">
                Pick a day out, <span className="font-accent text-amber-700">we'll do the rest.</span>
              </h1>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Hosted day-out packages with the private bus, professional driver and run sheet already sorted.
                Just bring the good vibes — wine trails, brewery crawls, long lunches and big nights out.
              </p>
            </motion.div>
          </div>
        </section>

        <section className="pb-20">
          <div className="max-w-7xl mx-auto px-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {tourPackages.map((t, i) => (
              <motion.div key={t.slug} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.07 }}>
                <Link to={`/tours/${t.slug}`} className="block bg-white rounded-2xl border border-black/5 overflow-hidden hover:shadow-xl hover:border-amber-300 transition-all group h-full" data-testid={`tour-card-${t.slug}`}>
                  <div className="relative h-52 overflow-hidden">
                    <img src={t.image} alt={t.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                    <div className="absolute top-3 left-3 px-3 py-1 rounded-full bg-gray-950/80 backdrop-blur text-amber-400 text-xs font-semibold flex items-center gap-1">
                      <MapPin className="h-3 w-3" /> {t.region}
                    </div>
                  </div>
                  <div className="p-6">
                    <h2 className="font-bold text-lg mb-1">{t.name}</h2>
                    <p className="text-sm text-muted-foreground mb-4">{t.tagline}</p>
                    <div className="flex items-center gap-4 text-xs text-muted-foreground mb-4">
                      <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5 text-amber-600" /> {t.duration}</span>
                      <span className="flex items-center gap-1"><Users className="h-3.5 w-3.5 text-amber-600" /> Up to 21 guests</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-amber-700 font-bold">From ${t.fromPrice} <span className="text-xs font-normal text-muted-foreground">/ group</span></span>
                      <span className="text-sm font-semibold text-amber-700 inline-flex items-center">View & book <ChevronRight className="h-4 w-4 ml-1" /></span>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </section>

        <section className="py-16 bg-gray-950 text-white text-center">
          <div className="max-w-3xl mx-auto px-4">
            <h2 className="text-2xl md:text-3xl font-bold mb-4">Want something <span className="font-accent text-amber-400">custom?</span></h2>
            <p className="text-gray-400 mb-8">Different region, bigger group, multi-day trip — tell us the occasion and we'll design the day around your crew.</p>
            <a href="https://wa.me/61481437772?text=Hi!%20I'd%20like%20to%20plan%20a%20custom%20group%20day%20out." target="_blank" rel="noreferrer">
              <Button size="lg" className="rounded-full px-10 bg-green-600 hover:bg-green-700 text-white">
                <MessageCircle className="mr-2 h-5 w-5" /> Plan a Custom Day
              </Button>
            </a>
          </div>
        </section>

        <SeoFooter />
      </div>
    </PageTransition>
  );
}
